import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { InfiniteScroll } from 'antd-mobile'

export interface GalleryProps<T> {
  render: (data: T) => React.ReactNode
  getKey?: keyof T | ((data: T) => keyof T)
  columns?: number
  onLoad: () => Promise<{ data: T[], hasMore: boolean}>
}

const Container = styled.div`
`
const FlexContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: start;
  & > * {
    flex: 1 1 1px;
  }
  & > * + * {
    margin-left: 0.5rem;
  }
`
const Column = styled.div`
  * + * {
    margin-top: 0.5rem;
  }
`
const Item = styled.div`
  border-radius: 0.25rem;
  overflow: hidden;
`

// TODO try to implement this via pure CSS with multiple columns layout
export function Gallery <T>({
  onLoad,
  columns = 2,
  render,
  getKey
}: GalleryProps<T>) {
  const columnRefs = useRef<{ [index: number]: HTMLDivElement }>({})
  const [distributed, setDistributed] = useState<T[][]>(() => {
    const state = []
    for (let i = 0; i < columns; i++) {
      state.push([])
    }
    return state
  })
  const [remaining, setRemaining] = useState<T[]>([])
  useLayoutEffect(() => {
    const [target, ...rest] = remaining
    if (target) {
      const [index] = Object
        .entries(columnRefs.current)
        .reduceRight((acc, cur) => {
          if (acc[1].scrollHeight < cur[1].scrollHeight) {
            return acc
          } else {
            return cur
          }
        })
      distributed[index as unknown as number].push(target)
      setDistributed(distributed)
      setRemaining(rest)
    }
  }, [remaining, setRemaining, setDistributed, distributed])
  const [hasMore, setHasMore] = useState(true)

  const load = useCallback(async () => {
    const { data, hasMore } = await onLoad()
    setHasMore(hasMore)
    setRemaining(data)
    return
  }, [setHasMore, setRemaining, onLoad])

  const key = useCallback((data: T) => {
    if (typeof getKey === 'string') {
      return String(data[getKey])
    } else if (typeof getKey === 'function') {
      return String(getKey(data))
    } else {
      return undefined
    }
  }, [getKey])
  return (
    <Container>
      <FlexContainer>
        {distributed.map((column, index) => (
          <Column
            key={index}
            ref={ref => {
              if (ref) {
                columnRefs.current[index] = ref
              }
            }}
          >
            {column.map((data, index) => <Item key={key(data) ?? index}>{render(data)}</Item>)}
          </Column>
        ))}

      </FlexContainer>
      <InfiniteScroll loadMore={load} hasMore={hasMore} />
    </Container>
  )
}
